import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Activity, BarChart3, PieChart, LineChart, Calendar, Download, Share2, RefreshCw, Filter, Eye, EyeOff, Maximize2, Minimize2, Settings, AlertTriangle, CheckCircle, Clock, Zap } from 'lucide-react';

const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['treasury', 'members', 'governance']);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [toastMessage, setToastMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'pdf'>('csv');

  const [analyticsData, setAnalyticsData] = useState({
    totalValue: 7.2,
    totalMembers: 247,
    monthlyYield: 15.7,
    activeHorses: 12,
    governanceParticipation: 89.3,
    treasuryGrowth: 23.5
  });

  const [chartData, setChartData] = useState({
    treasury: [
      { date: '2024-01-01', value: 5.2 },
      { date: '2024-01-08', value: 5.8 },
      { date: '2024-01-15', value: 6.1 },
      { date: '2024-01-22', value: 6.5 },
      { date: '2024-01-29', value: 6.9 },
      { date: '2024-02-05', value: 7.2 }
    ],
    members: [
      { date: '2024-01-01', count: 180 },
      { date: '2024-01-08', count: 195 },
      { date: '2024-01-15', count: 210 },
      { date: '2024-01-22', count: 225 },
      { date: '2024-01-29', count: 235 },
      { date: '2024-02-05', count: 247 }
    ],
    yield: [
      { date: '2024-01-01', rate: 12.3 },
      { date: '2024-01-08', rate: 13.1 },
      { date: '2024-01-15', rate: 13.8 },
      { date: '2024-01-22', rate: 14.5 },
      { date: '2024-01-29', rate: 15.2 },
      { date: '2024-02-05', rate: 15.7 }
    ]
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'treasury', label: 'Treasury', icon: DollarSign },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'governance', label: 'Governance', icon: Activity }
  ];

  const timeRanges = [
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const kpiCards = [
    {
      title: 'Total Treasury Value',
      value: `$${analyticsData.totalValue}M`,
      change: '+23.5%',
      changeType: 'positive',
      icon: DollarSign
    },
    {
      title: 'Active Members',
      value: analyticsData.totalMembers.toLocaleString(),
      change: '+12.3%',
      changeType: 'positive',
      icon: Users
    },
    {
      title: 'Monthly Yield',
      value: `${analyticsData.monthlyYield}%`,
      change: '+2.1%',
      changeType: 'positive',
      icon: TrendingUp
    },
    {
      title: 'Governance Participation',
      value: `${analyticsData.governanceParticipation}%`,
      change: '+5.7%',
      changeType: 'positive',
      icon: Activity
    }
  ];

  const generateChartData = (type: string) => {
    const data = chartData[type as keyof typeof chartData];
    return data.map((point, index) => ({
      x: index,
      y: point.value || point.count || point.rate,
      label: new Date(point.date).toLocaleDateString()
    }));
  };

  const generateTimeBasedData = (range: string) => {
    const now = new Date();
    const ranges = {
      '24h': { days: 1, points: 24, interval: 'hour' },
      '7d': { days: 7, points: 7, interval: 'day' },
      '30d': { days: 30, points: 30, interval: 'day' },
      '90d': { days: 90, points: 12, interval: 'week' },
      '1y': { days: 365, points: 12, interval: 'month' }
    };
    
    const config = ranges[range as keyof typeof ranges] || ranges['7d'];
    const multiplier = config.days / 7; // Scale based on time range
    
    return {
      treasury: {
        totalValue: 7.2 * (1 + (Math.random() - 0.5) * 0.2 * multiplier),
        suiStaked: 4.2 * (1 + (Math.random() - 0.5) * 0.1 * multiplier),
        usdcReserve: 1.8 * (1 + (Math.random() - 0.5) * 0.1 * multiplier),
        horseAssets: 1.2 * (1 + (Math.random() - 0.5) * 0.1 * multiplier),
        apy: 15.7 + (Math.random() - 0.5) * 2 * multiplier,
        dailyYield: 2847 * (1 + (Math.random() - 0.5) * 0.3 * multiplier),
        monthlyYield: 86500 * (1 + (Math.random() - 0.5) * 0.2 * multiplier)
      },
      members: {
        totalMembers: Math.floor(247 * (1 + (Math.random() - 0.5) * 0.1 * multiplier)),
        activeMembers: Math.floor(221 * (1 + (Math.random() - 0.5) * 0.1 * multiplier)),
        newMembersToday: Math.floor(3 * (1 + (Math.random() - 0.5) * 0.5 * multiplier)),
        averageInvestment: Math.floor(12500 * (1 + (Math.random() - 0.5) * 0.1 * multiplier))
      },
      governance: {
        activeProposals: Math.floor(3 * (1 + (Math.random() - 0.5) * 0.3 * multiplier)),
        totalProposals: Math.floor(24 * (1 + (Math.random() - 0.5) * 0.1 * multiplier)),
        participationRate: 89.3 + (Math.random() - 0.5) * 5 * multiplier,
        votingPower: Math.floor(247 * (1 + (Math.random() - 0.5) * 0.05 * multiplier))
      }
    };
  };

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      handleRefresh();
    }, refreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const handleTimeRangeChange = async (newRange: string) => {
    setIsLoading(true);
    setTimeRange(newRange);
    
    // Simulate data loading
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newData = generateTimeBasedData(newRange);
    setAnalyticsData(prev => ({
      ...prev,
      ...newData.treasury,
      ...newData.members,
      ...newData.governance
    }));
    
    setIsLoading(false);
    setToastMessage(`Data updated for ${newRange} timeframe`);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const newData = generateTimeBasedData(timeRange);
      setAnalyticsData(prev => ({
        ...prev,
        ...newData.treasury,
        ...newData.members,
        ...newData.governance
      }));
      setToastMessage('Data refreshed successfully');
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Failed to refresh data');
      setTimeout(() => setToastMessage(''), 3000);
    }
    setIsLoading(false);
  };

  const handleExport = () => {
    const data = {
      timestamp: new Date().toISOString(),
      timeframe: timeRange,
      data: analyticsData
    };
    
    if (exportFormat === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sui-ndicate-analytics-${timeRange}.json`;
      a.click();
    } else if (exportFormat === 'csv') {
      const csv = `Metric,Value,Timestamp\nTreasury Value,${analyticsData.totalValue}M SUI,${new Date().toISOString()}\nTotal Members,${analyticsData.totalMembers},${new Date().toISOString()}\nAPY,${analyticsData.apy}%,${new Date().toISOString()}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sui-ndicate-analytics-${timeRange}.csv`;
      a.click();
    }
    
    setToastMessage(`Data exported as ${exportFormat.toUpperCase()}`);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'SUI-ndicate Analytics',
        text: `Check out our syndicate performance: ${analyticsData.totalValue}M SUI treasury, ${analyticsData.totalMembers} members, ${analyticsData.apy}% APY`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setToastMessage('Link copied to clipboard');
      setTimeout(() => setToastMessage(''), 3000);
    }
  };

  const toggleMetric = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  return (
    <div className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 ${isFullscreen ? 'fixed inset-0 z-50 bg-slate-900' : ''}`}>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slideIn">
          {toastMessage}
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
                  <p className="text-slate-400">Real-time insights into syndicate performance</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                  >
                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Advanced Controls */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <select
                    value={timeRange}
                    onChange={(e) => handleTimeRangeChange(e.target.value)}
                    disabled={isLoading}
                    className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {timeRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleRefresh}
                    disabled={isLoading}
                    className="flex items-center space-x-2 bg-slate-700 text-slate-300 px-3 py-2 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50 text-sm"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                      autoRefresh 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <Zap className="w-4 h-4" />
                    <span>Auto-refresh</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center space-x-2 bg-slate-700 text-slate-300 px-3 py-2 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                  >
                    <Filter className="w-4 h-4" />
                    <span>Filters</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleExport}
                    className="flex items-center space-x-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 px-3 py-2 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleShare}
                    className="flex items-center space-x-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 px-3 py-2 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                </div>
              </div>
              
              {isLoading && (
                <div className="mt-4 text-cyan-400 text-sm flex items-center">
                  <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading data...
                </div>
              )}
            </div>
          </div>
          
          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">Advanced Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Metrics to Display</label>
                  <div className="space-y-2">
                    {['treasury', 'members', 'governance'].map((metric) => (
                      <label key={metric} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedMetrics.includes(metric)}
                          onChange={() => toggleMetric(metric)}
                          className="w-4 h-4 text-cyan-500 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                        />
                        <span className="text-slate-300 capitalize">{metric}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Auto-refresh Interval</label>
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  >
                    <option value={10}>10 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Export Format</label>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json' | 'pdf')}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="csv">CSV</option>
                    <option value="json">JSON</option>
                    <option value="pdf">PDF</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpiCards.map((card, index) => (
            <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-200 group">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-cyan-500/20 rounded-lg group-hover:bg-cyan-500/30 transition-colors">
                    <card.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="text-slate-400 text-sm">{card.title}</div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    card.changeType === 'positive' ? 'bg-green-500' : 'bg-red-500'
                  } animate-pulse`}></div>
                  <span className={`text-xs font-medium ${
                    card.changeType === 'positive' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {card.change}
                  </span>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">{card.value}</h3>
              <div className="flex items-center justify-between">
                <div className="text-slate-400 text-sm">Last updated</div>
                <div className="text-slate-500 text-xs">{new Date().toLocaleTimeString()}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Navigation Tabs */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex space-x-1 bg-slate-700/50 rounded-lg p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-cyan-500 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            
            {/* Real-time Status Indicator */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-slate-400 text-sm">Live Data</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-400 text-sm">Updated {new Date().toLocaleTimeString()}</span>
              </div>
              {autoRefresh && (
                <div className="flex items-center space-x-2">
                  <Zap className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm">Auto-refresh: {refreshInterval}s</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Treasury Growth Chart */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Treasury Growth</h2>
                <div className="flex items-center space-x-2 text-green-400">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm font-medium">+23.5%</span>
                </div>
              </div>
              <div className="h-64 bg-slate-700/30 rounded-lg p-4 flex items-end space-x-2">
                {generateChartData('treasury').map((point, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t"
                      style={{ height: `${(point.y / 8) * 200}px` }}
                    ></div>
                    <span className="text-xs text-slate-400 mt-2">{point.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Member Growth Chart */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Member Growth</h2>
                <div className="flex items-center space-x-2 text-green-400">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-medium">+12.3%</span>
                </div>
              </div>
              <div className="h-64 bg-slate-700/30 rounded-lg p-4 flex items-end space-x-2">
                {generateChartData('members').map((point, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t"
                      style={{ height: `${(point.y / 300) * 200}px` }}
                    ></div>
                    <span className="text-xs text-slate-400 mt-2">{point.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'treasury' && (
          <div className="space-y-8">
            {/* Treasury Allocation */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-6">Treasury Allocation</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {[
                    { label: 'SUI Staked', value: 4.2, percentage: 58, color: 'cyan' },
                    { label: 'USDC Reserve', value: 1.8, percentage: 25, color: 'blue' },
                    { label: 'Horse Assets', value: 1.2, percentage: 17, color: 'green' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 bg-${item.color}-500 rounded-full`}></div>
                        <span className="text-white font-medium">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold">${item.value}M</div>
                        <div className="text-slate-400 text-sm">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <svg className="w-48 h-48 transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        fill="none"
                        stroke="rgb(30 41 59)"
                        strokeWidth="16"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        fill="none"
                        stroke="rgb(6 182 212)"
                        strokeWidth="16"
                        strokeDasharray={`${2 * Math.PI * 80}`}
                        strokeDashoffset={`${2 * Math.PI * 80 * (1 - 0.58)}`}
                        className="transition-all duration-1000"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        fill="none"
                        stroke="rgb(59 130 246)"
                        strokeWidth="16"
                        strokeDasharray={`${2 * Math.PI * 80}`}
                        strokeDashoffset={`${2 * Math.PI * 80 * (1 - 0.25)}`}
                        className="transition-all duration-1000"
                        style={{ strokeDashoffset: `${2 * Math.PI * 80 * (1 - 0.25)}` }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">$7.2M</div>
                        <div className="text-slate-400 text-sm">Total Value</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Yield Performance */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-6">Yield Performance</h2>
              <div className="h-64 bg-slate-700/30 rounded-lg p-4 flex items-end space-x-2">
                {generateChartData('yield').map((point, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t"
                      style={{ height: `${(point.y / 20) * 200}px` }}
                    ></div>
                    <span className="text-xs text-slate-400 mt-2">{point.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="space-y-8">
            {/* Member Demographics */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Geographic Distribution</h3>
                <div className="space-y-3">
                  {[
                    { region: 'North America', percentage: 45, count: 111 },
                    { region: 'Europe', percentage: 32, count: 79 },
                    { region: 'Asia', percentage: 18, count: 44 },
                    { region: 'Other', percentage: 5, count: 13 }
                  ].map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">{item.region}</span>
                        <span className="text-white text-sm font-medium">{item.percentage}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Investment Tiers</h3>
                <div className="space-y-3">
                  {[
                    { tier: 'Whale (>$100K)', count: 12, color: 'purple' },
                    { tier: 'Large ($10K-$100K)', count: 45, color: 'blue' },
                    { tier: 'Medium ($1K-$10K)', count: 89, color: 'cyan' },
                    { tier: 'Small (<$1K)', count: 101, color: 'green' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-slate-300 text-sm">{item.tier}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-medium">{item.count}</span>
                        <div className={`w-3 h-3 bg-${item.color}-500 rounded-full`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Activity Level</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">89.3%</div>
                    <div className="text-slate-400 text-sm">Active Members</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Daily Active</span>
                      <span className="text-white">156</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Weekly Active</span>
                      <span className="text-white">198</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Monthly Active</span>
                      <span className="text-white">221</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'governance' && (
          <div className="space-y-8">
            {/* Governance Metrics */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Proposal Activity</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Total Proposals</span>
                    <span className="text-white font-bold">24</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Active Proposals</span>
                    <span className="text-cyan-400 font-bold">3</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Passed Proposals</span>
                    <span className="text-green-400 font-bold">18</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Rejected Proposals</span>
                    <span className="text-red-400 font-bold">3</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Voting Participation</h3>
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">89.3%</div>
                    <div className="text-slate-400 text-sm">Average Participation</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">High Participation</span>
                      <span className="text-green-400">15 proposals</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Medium Participation</span>
                      <span className="text-yellow-400">6 proposals</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Low Participation</span>
                      <span className="text-red-400">3 proposals</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Proposals */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Recent Proposals</h3>
              <div className="space-y-4">
                {[
                  {
                    title: 'Acquire Thunder Bay for $2.5M',
                    status: 'active',
                    votes: 198,
                    participation: 89.3,
                    deadline: '3 days'
                  },
                  {
                    title: 'Increase Treasury Allocation to 70%',
                    status: 'passed',
                    votes: 221,
                    participation: 92.1,
                    deadline: 'Completed'
                  },
                  {
                    title: 'Implement New Governance Framework',
                    status: 'active',
                    votes: 156,
                    participation: 78.5,
                    deadline: '7 days'
                  }
                ].map((proposal, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{proposal.title}</h4>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-slate-400">
                        <span>{proposal.votes} votes</span>
                        <span>{proposal.participation}% participation</span>
                        <span>Deadline: {proposal.deadline}</span>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      proposal.status === 'active' 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {proposal.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
